import React, { useState } from 'react';
import { useTheme } from './ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function SearchPage({ userId }) {
  const { theme } = useTheme();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [aiSummary, setAiSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [useAI, setUseAI] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setSearched(true);
    setResults([]);
    setAiSummary('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          query: query,
          use_ai: useAI,
        }),
      });

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      setResults(data.results || []);
      if (useAI && data.ai_summary) {
        setAiSummary(data.ai_summary);
      }
    } catch (error) {
      console.error('Search error:', error);
      alert('Failed to search. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const highlightText = (text, query) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={i} style={styles.highlight}>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const styles = getStyles(theme);

  return (
    <div style={styles.container}>
      <div style={styles.searchBox}>
        <h1 style={styles.title}>Search Your Thoughts</h1>
        <p style={styles.subtitle}>Find entries from your voice journal</p>

        <form onSubmit={handleSearch} style={styles.searchForm}>
          <div style={styles.inputWrapper}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="What are you looking for?"
              style={styles.searchInput}
              autoFocus
            />
            <button type="submit" style={styles.searchButton} disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>

          <div style={styles.optionsRow}>
            <label style={styles.checkbox}>
              <input
                type="checkbox"
                checked={useAI}
                onChange={(e) => setUseAI(e.target.checked)}
                style={styles.checkboxInput}
              />
              <span style={styles.checkboxLabel}>
                Use AI Summary
              </span>
            </label>
          </div>
        </form>
      </div>

      {loading && (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Searching your journal...</p>
        </div>
      )}

      {!loading && searched && (
        <div style={styles.resultsContainer}>
          {aiSummary && (
            <div style={styles.aiSummaryBox}>
              <h3 style={styles.aiSummaryTitle}>AI Summary</h3>
              <p style={styles.aiSummaryText}>{aiSummary}</p>
            </div>
          )}

          <div style={styles.resultsInfo}>
            {results.length} {results.length === 1 ? 'result' : 'results'} for "{query}"
          </div>

          {results.length === 0 ? (
            <div style={styles.noResults}>
              <div style={styles.noResultsIcon}>∅</div>
              <p style={styles.noResultsText}>
                No results found for "{query}"
              </p>
              <p style={styles.noResultsHint}>
                Try different keywords or check your spelling
              </p>
            </div>
          ) : (
            <div style={styles.resultsList}>
              {results.map((result, index) => (
                <div key={index} style={styles.resultCard}>
                  <div style={styles.resultHeader}>
                    <span style={styles.resultDate}>
                      {formatDate(result.created_at)}
                    </span>
                    {result.similarity && (
                      <span style={styles.relevanceScore}>
                        {Math.round(result.similarity * 100)}% match
                      </span>
                    )}
                  </div>
                  <p style={styles.resultContent}>
                    {highlightText(result.content, query)}
                  </p>
                  {result.is_triggered && (
                    <span style={styles.triggerBadge}>Triggered Entry</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const getStyles = (theme) => ({
  container: {
    width: '100%',
    maxWidth: '900px',
    margin: '0 auto',
    padding: '16px',
  },
  searchBox: {
    marginBottom: '32px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '800',
    marginBottom: '6px',
    color: theme.text,
    letterSpacing: '-0.5px',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '14px',
    color: theme.textMuted,
    marginBottom: '24px',
    textAlign: 'center',
  },
  searchForm: {
    width: '100%',
  },
  inputWrapper: {
    display: 'flex',
    gap: '10px',
    marginBottom: '14px',
    flexWrap: 'wrap',
  },
  searchInput: {
    flex: 1,
    minWidth: '180px',
    padding: '14px 16px',
    fontSize: '16px',
    borderRadius: '12px',
    border: `2px solid ${theme.border}`,
    backgroundColor: theme.surface,
    color: theme.text,
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  searchButton: {
    padding: '14px 28px',
    fontSize: '15px',
    borderRadius: '12px',
    border: 'none',
    background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
    color: '#FFFFFF',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: `0 4px 16px rgba(0, 212, 106, 0.3)`,
    whiteSpace: 'nowrap',
  },
  optionsRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  checkboxInput: {
    width: '18px',
    height: '18px',
    marginRight: '8px',
    cursor: 'pointer',
    accentColor: theme.primary,
  },
  checkboxLabel: {
    fontSize: '15px',
    color: theme.textSecondary,
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '48px 16px',
  },
  spinner: {
    width: '44px',
    height: '44px',
    border: `4px solid ${theme.border}`,
    borderTop: `4px solid ${theme.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 16px',
  },
  loadingText: {
    color: theme.textMuted,
    fontSize: '15px',
  },
  resultsContainer: {
    marginTop: '16px',
  },
  aiSummaryBox: {
    background: theme.surface,
    border: `2px solid ${theme.primary}`,
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '24px',
    boxShadow: `0 8px 32px rgba(0, 212, 106, 0.15)`,
  },
  aiSummaryTitle: {
    fontSize: '17px',
    fontWeight: '600',
    color: theme.primary,
    marginBottom: '10px',
  },
  aiSummaryText: {
    fontSize: '14px',
    lineHeight: '1.7',
    color: theme.textSecondary,
    margin: 0,
  },
  resultsInfo: {
    fontSize: '13px',
    color: theme.textMuted,
    marginBottom: '16px',
    textAlign: 'center',
  },
  noResults: {
    textAlign: 'center',
    padding: '48px 16px',
  },
  noResultsIcon: {
    fontSize: '56px',
    color: theme.border,
    marginBottom: '12px',
  },
  noResultsText: {
    fontSize: '16px',
    color: theme.text,
    marginBottom: '6px',
  },
  noResultsHint: {
    fontSize: '13px',
    color: theme.textMuted,
  },
  resultsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  resultCard: {
    background: theme.surface,
    padding: '16px',
    borderRadius: '14px',
    border: `1px solid ${theme.border}`,
    transition: 'border-color 0.2s, transform 0.2s',
    cursor: 'pointer',
  },
  resultHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
    flexWrap: 'wrap',
    gap: '6px',
  },
  resultDate: {
    fontSize: '12px',
    color: theme.textMuted,
  },
  relevanceScore: {
    fontSize: '11px',
    color: theme.primary,
    backgroundColor: 'rgba(0, 212, 106, 0.1)',
    padding: '4px 8px',
    borderRadius: '10px',
    fontWeight: '600',
  },
  resultContent: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: theme.textSecondary,
    margin: '0 0 10px 0',
  },
  highlight: {
    backgroundColor: theme.primary,
    color: '#FFFFFF',
    padding: '2px 6px',
    borderRadius: '4px',
    fontWeight: '600',
  },
  triggerBadge: {
    display: 'inline-block',
    fontSize: '11px',
    color: theme.primary,
    backgroundColor: 'rgba(0, 212, 106, 0.1)',
    padding: '4px 8px',
    borderRadius: '10px',
    fontWeight: '600',
  },
});

export default SearchPage;
